import React, { useState, useEffect } from 'react';
import { Layout, Typography, Button, Spin, message, Modal, Space } from 'antd';
import {
  CheckOutlined,
  StarOutlined,
  FolderOutlined,
  SafetyOutlined,
  RocketOutlined,
  TeamOutlined,
  MessageOutlined,
  SyncOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { billingService } from '../services/billingService';
import { useBillingStore } from '../stores/billingStore';
import { useAuthStore } from '../stores/authStore';
import '../css/MembershipPage.css';

const { Content } = Layout;
const { Text, Paragraph } = Typography;

const MembershipPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { fetchSubscriptionConfig, subscriptionConfig } = useBillingStore();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);

  // Fetch subscription status and config
  useEffect(() => {
    const init = async () => {
      if (!isAuthenticated) {
        message.warning('Please login to view your membership');
        navigate('/login?returnUrl=/membership');
        return;
      }

      try {
        setLoading(true);

        // Fetch subscription config and status in parallel
        const [config, status] = await Promise.all([
          fetchSubscriptionConfig(),
          billingService.getSubscriptionStatus({ includePaymentMethod: true }),
        ]);

        // status
        // hasSubscription: boolean,
        // status: string,
        // priceId: string,
        // currentPeriodEnd: date,
        // cancelAtPeriodEnd: boolean,
        
        setSubscription(status);

        // Initialize Stripe
        const key = config?.publishableKey;
        if (key) {
          setStripePromise(loadStripe(key));
        }
      } catch (error) {
        console.error('Failed to load membership data:', error);
        message.error('Failed to load membership information');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [isAuthenticated, navigate, fetchSubscriptionConfig]);

  // Handle upgrade to Professional
  const handleUpgrade = async () => {
    if (!stripePromise) {
      message.error('Payment system is not ready');
      return;
    }

    const proPriceId = subscriptionConfig?.proPrice;
    if (!proPriceId) {
      message.error('Professional plan is not configured');
      return;
    }

    setActionLoading(true);
    try {
      const sessionResp = await billingService.createSubscriptionCheckoutSession({
        priceId: proPriceId
      });

      const sessionId = sessionResp?.sessionId || sessionResp?.data?.sessionId;
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        message.error(`Payment failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      message.error('Failed to start upgrade process');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel subscription
  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      const result = await billingService.cancelSubscription();
      message.success(result.message || 'Subscription will be canceled at the end of the billing period');

      const status = await billingService.getSubscriptionStatus({ includePaymentMethod: true });
      setSubscription(status);
      setCancelModalVisible(false);
    } catch (error) {
      console.error('Cancel error:', error);
      message.error(error.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reactivate subscription
  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      const result = await billingService.reactivateSubscription();
      message.success(result.message || 'Subscription reactivated successfully');

      const status = await billingService.getSubscriptionStatus({ includePaymentMethod: true });
      setSubscription(status);
    } catch (error) {
      console.error('Reactivate error:', error);
      message.error(error.response?.data?.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update payment method
  const handleUpdatePaymentMethod = async () => {
    setActionLoading(true);
    try {
      const result = await billingService.createCustomerPortalSession();
      // Redirect to Stripe customer portal
      window.location.href = result.url;
    } catch (error) {
      console.error('Update payment method error:', error);
      message.error(error.response?.data?.message || 'Failed to open payment settings');
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout className="membership-page">
        <div className="loading-container">
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: 16 }}>Loading membership information...</Text>
        </div>
      </Layout>
    );
  }

  const isPro = subscription?.hasSubscription;
  const isBasic = !isPro;
  const isCanceling = isPro && subscription?.cancelAtPeriodEnd;

  return (
    <Layout className="membership-page">
      {/* Back Button */}
      <div style={{ padding: '20px 24px 0' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          style={{ marginBottom: '16px' }}
        >
          Back to Home
        </Button>
      </div>

      {/* Page Title */}
      <div className="membership-page-title">
        <h1>{user?.username || user?.email}'s Memberships & Subscriptions</h1>
      </div>

      <Content className="membership-content">
        <div className="membership-content-inner">
          {/* Cancellation Warning */}
          {isCanceling && (
            <div className="cancellation-warning">
              <ExclamationCircleOutlined className="warning-icon" />
              <div className="warning-content">
                <div className="warning-title">Subscription Ending</div>
                <div className="warning-text">
                  Your Professional membership will end on {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}. You'll have access to Professional features until then.
                </div>
              </div>
            </div>
          )}

          {/* Current Subscription */}
          {isPro && (
            <div className="current-subscription-card">
              <div className="subscription-header">
                <div className="subscription-logo">
                  <span>Professional</span>
                </div>
              </div>

              <div className="subscription-body">
                <div className="subscription-grid">
                  <div className="subscription-section">
                    <div className="section-label">Renewal date</div>
                    <div className="section-value">
                      {isCanceling ? 'Ends on' : 'Renews on'} {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>

                  <div className="subscription-section">
                    <div className="section-label">Payment method</div>
                    <div className="payment-method-display">
                      <CreditCardOutlined className="card-icon" />
                      <span className="section-value">
                        {subscription?.paymentMethod
                          ? `${subscription.paymentMethod.brand?.charAt(0).toUpperCase() + subscription.paymentMethod.brand?.slice(1)} ending in ${subscription.paymentMethod.last4}`
                          : 'No payment method on file'
                        }
                      </span>
                    </div>
                    <a
                      href="#"
                      className="change-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUpdatePaymentMethod();
                      }}
                    >
                      Change
                    </a>
                  </div>
                </div>

                <div className="subscription-actions">
                  {!isCanceling && (
                    <Button
                      className="action-button-danger"
                      onClick={() => setCancelModalVisible(true)}
                    >
                      End Membership
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Available Plans */}
          <div className="available-plans-section">
            <h2 className="section-title">Available Plans</h2>

            <div className="plans-grid">
              {/* Starter Plan */}
              <div className={`plan-card ${isBasic ? 'current-plan' : ''}`}>
                {isBasic && <div className="current-plan-badge">CURRENT PLAN</div>}

                <div className="plan-header">
                  <h3 className="plan-name">
                    Starter — Your Personal Knowledge Hub
                  </h3>
                  <div className="plan-price">
                    <span className="currency">$</span>
                    <span className="amount">0</span>
                    <span className="period">/month</span>
                  </div>
                  <div className="plan-description">Everything you need to organize your knowledge.</div>
                </div>

                <div className="plan-features">
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>📎 Clip web pages into your knowledge hub</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>📄 Upload and store documents</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🔍 Full-text keyword search across all your knowledge cards</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🔍 Semantic search — find ideas by meaning, not just keywords</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>⭐ Edit, favorite, and organize your knowledge cards</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🌐 Browse and search by tags and topics</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🤝 Share knowledge cards with friends</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🎉 10 free LLM assists — try Professional AI advanced features</span>
                  </div>
                </div>

                <div className="plan-action">
                  {isBasic ? (
                    <Button className="current-plan-button" disabled>
                      Current Plan
                    </Button>
                  ) : (
                    <Text type="secondary" style={{ textAlign: 'center', display: 'block', fontSize: '13px' }}>
                      Available after cancellation
                    </Text>
                  )}
                </div>
              </div>

              {/* Professional Plan */}
              <div className={`plan-card recommended ${isPro && !isCanceling ? 'current-plan' : ''}`}>
                {isPro && !isCanceling ? (
                  <div className="current-plan-badge">CURRENT PLAN</div>
                ) : (
                  <div className="plan-badge">RECOMMENDED</div>
                )}

                <div className="plan-header">
                  <h3 className="plan-name">
                    Professional — Smarter Knowledge with AI
                  </h3>
                  <div className="plan-price">
                    <span className="currency">$</span>
                    <span className="amount">4.99</span>
                    <span className="period">/month</span>
                  </div>
                  <div className="plan-description">Turn the things you read, learn, and discover into your own knowledge hub — searchable, organized, and powered by AI.</div>
                </div>

                <div className="plan-features">
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span><strong>Everything in Starter, plus:</strong></span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>✨ AI-generated summaries for quick understanding</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>📁 Automatic categorization by topic</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>💬 Ask questions and get answers from your own knowledge hub</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🏷️ Auto-tagging and topic discovery</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>📖 AI Read Assistant</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>💡 Ideas Brainstorm with AI</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>🧠 AI Quiz and Doc generation</span>
                  </div>
                  <div className="feature-item">
                    <CheckOutlined className="feature-icon available" />
                    <span>⚡ 400 LLM assists included each month</span>
                  </div>
                </div>

                <div className="plan-action">
                  {isPro && !isCanceling ? (
                    <Button className="current-plan-button" disabled>
                      Current Plan
                    </Button>
                  ) : isCanceling ? (
                    <Button
                      className="select-plan-button"
                      loading={actionLoading}
                      onClick={handleReactivate}
                    >
                      Reactivate Professional
                    </Button>
                  ) : (
                    <Button
                      className="select-plan-button"
                      loading={actionLoading}
                      onClick={handleUpgrade}
                    >
                      Upgrade to Professional
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="benefits-section">
            <h2 className="section-title">Professional Membership Benefits</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <RocketOutlined className="benefit-icon" />
                <div className="benefit-title">Collect Knowledge from Everywhere</div>
                <div className="benefit-description">
                  Bring your ideas and information together in one place — from the web, local files, or uploads in Word, PowerPoint, or PDF format. Your documents become AI-ready, neatly organized, and instantly retrievable by AI tools.
                </div>
              </div>
              <div className="benefit-item">
                <StarOutlined className="benefit-icon" />
                <div className="benefit-title">AI Auto-Summarization</div>
                <div className="benefit-description">
                  Save time and focus on what matters most. AI automatically summarizes your documents and generates tags, so you get the key points at a glance.
                </div>
              </div>
              <div className="benefit-item">
                <FolderOutlined className="benefit-icon" />
                <div className="benefit-title">AI Auto-Categorization</div>
                <div className="benefit-description">
                  No more messy folders or scattered files. Let AI auto-categorize your knowledge so you can browse, search, and retrieve information effortlessly.
                </div>
              </div>
              <div className="benefit-item">
                <TeamOutlined className="benefit-icon" />
                <div className="benefit-title">Priority Support</div>
                <div className="benefit-description">
                  24/7 priority email support
                </div>
              </div>
              <div className="benefit-item">
                <MessageOutlined className="benefit-icon" />
                <div className="benefit-title">Ask Questions with AI</div>
                <div className="benefit-description">
                  Go beyond reading — start understanding. Ask questions directly on any knowledge card, and AI will help you analyze, clarify, and explore insights from your own content.
                </div>
              </div>
              <div className="benefit-item">
                <SyncOutlined className="benefit-icon" />
                <div className="benefit-title">Auto-sync & Backup</div>
                <div className="benefit-description">
                  Automatic cloud sync and daily backups
                </div>
              </div>
            </div>
          </div>

          {/* Secure Payment Notice */}
          <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '24px', borderTop: '1px solid #e7e7e7' }}>
            <Text type="secondary">
              <SafetyOutlined /> Secure payments processed by Stripe
            </Text>
          </div>
        </div>
      </Content>

      {/* Cancel Confirmation Modal */}
      <Modal
        title="End Professional Membership?"
        open={cancelModalVisible}
        onCancel={() => setCancelModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setCancelModalVisible(false)}>
            Keep Membership
          </Button>,
          <Button
            key="submit"
            danger
            type="primary"
            loading={actionLoading}
            onClick={handleCancelSubscription}
          >
            End Membership
          </Button>,
        ]}
        width={500}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph>
            Are you sure you want to end your Professional membership? Here's what will happen:
          </Paragraph>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>You'll have Professional benefits until {subscription?.currentPeriodEnd && new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</li>
            <li>No more charges after this billing period</li>
            <li>You'll automatically switch to the Starter plan</li>
            <li>You can reactivate anytime before the end date</li>
          </ul>
        </Space>
      </Modal>
    </Layout>
  );
};

export default MembershipPage;
